import { localizedAccountDisplayName } from "../utils/localizedAccountDisplayName";
// src/pages/TrialBalance.tsx
import { accountDisplayName } from "../utils/accountDisplayName";
import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { apiGet } from "../api/client";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeftIcon,
  XMarkIcon,
  EllipsisVerticalIcon,
} from "@heroicons/react/24/solid";
import { companyRoute } from "../utils/routeHelpers";
import { ymdTodayLocal } from "../utils/dateUtils";
import { AppleComboBox } from "../components/AppleComboBox";
import { downloadCsv } from "../utils/downloadCsv";
import { formatNumber } from "../utils/formatNumber";
import { A } from "../styles/appleClasses";
import { ReportDateRangeFilter } from "../components/ReportDateRangeFilter";

// --- Types ---
type TrialBalanceRow = {
  account_id: number;
  account_code?: string | number | null;
  account_name: string;
  type?: string | null;
  kind?: string | null;
  debit?: number;
  credit?: number;
  balance?: number; // Dr - Cr
};

type ApiPayload =
  | TrialBalanceRow[]
  | {
      rows?: TrialBalanceRow[];
      as_of?: string;
      period?: { start: string; end: string };
    };


// --- Helpers ---
function fmt(n: number) {
  return formatNumber(Number(n || 0));
}

function todayStr() {
  return ymdTodayLocal();
}

type SortMode = "alpha" | "class";
type SortOption = { id: SortMode; name: string };

function normalizeType(t?: string | null) {
  const s = (t || "").toLowerCase().trim();
  if (!s) return "";
  if (s.includes("asset")) return "asset";
  if (s.includes("liab")) return "liability";
  if (s.includes("equity")) return "equity";
  if (s.includes("income") || s.includes("revenue")) return "income";
  if (s.includes("expense")) return "expense";
  if (s.includes("cogs") || s.includes("cost")) return "cogs";
  return s;
}

function normalizeText(v?: string | number | null) {
  return String(v ?? "")
    .toLowerCase()
    .trim();
}

function compareText(a?: string | number | null, b?: string | number | null) {
  return String(a ?? "").localeCompare(String(b ?? ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

function displayAccountName(name: string) {
  return String(name || "")
    .replace(/Shareholder Advance/gi, "Owner Advance")
    .replace(/Shareholder Capital/gi, "Owner Capital")
    .replace(/Due from Shareholder/gi, "Owner Withdrawal");
}

function accountCodeSortKey(code?: string | number | null) {
  const raw = String(code ?? "").trim();
  if (!raw) return "\uffff";

  const parts = raw.match(/[A-Za-z]+|\d+/g);
  if (!parts || !parts.length) return raw.toLowerCase();

  return parts
    .map((p) => (/^\d+$/.test(p) ? p.padStart(12, "0") : p.toLowerCase()))
    .join("|");
}

function compareAccountCode(
  a?: string | number | null,
  b?: string | number | null,
) {
  return accountCodeSortKey(a).localeCompare(accountCodeSortKey(b), undefined, {
    sensitivity: "base",
  });
}

/**
 * Trial Balance class sort should mirror report page order:
 *
 * Balance Sheet:
 *  0 Cash
 *  1 Bank
 *  2 Receivables
 *  3 Inventory
 *  4 Other Assets
 *  5 Payables
 *  6 Other Liabilities
 *  7 Capital
 *  8 Other Equity
 *  9 Retained Earnings
 *
 * Income Statement:
 * 10 Revenue
 * 11 COGS
 * 12 Operating Expenses
 * 13 Other Income
 * 14 Other Expense
 * 99 Unknown
 */
function reportBucket(row: TrialBalanceRow) {
  const type = normalizeType(row.type);
  const kind = normalizeText(row.kind);
  const name = normalizeText(row.account_name);

  // ---------------- Assets ----------------
  if (type === "asset") {
    if (
      kind.includes("cash") ||
      name === "cash" ||
      name.includes("petty cash") ||
      name.includes("cash on hand")
    ) {
      return 0; // Cash
    }

    if (
      kind.includes("bank") ||
      name.includes("bank") ||
      name.includes("checking") ||
      name.includes("savings")
    ) {
      return 1; // Bank
    }

    if (
      kind.includes("receivable") ||
      kind.includes("ar") ||
      name.includes("receivable") ||
      name.includes("a/r")
    ) {
      return 2; // Receivables
    }

    if (
      kind.includes("inventory") ||
      name.includes("inventory") ||
      name.includes("stock")
    ) {
      return 3; // Inventory
    }

    return 4; // Other Assets
  }

  // -------------- Liabilities --------------
  if (type === "liability") {
    if (
      kind.includes("payable") ||
      kind.includes("ap") ||
      name.includes("payable") ||
      name.includes("a/p")
    ) {
      return 5; // Payables
    }

    return 6; // Other Liabilities
  }

  // ---------------- Equity ----------------
  if (type === "equity") {
    if (
      name.includes("retained earnings") ||
      name.includes("retained earning")
    ) {
      return 9; // Retained Earnings
    }

    if (
      name.includes("capital") ||
      name.includes("owner") ||
      name.includes("partner")
    ) {
      return 7; // Capital
    }

    return 8; // Other Equity
  }

  // ----------- Income Statement -----------
  if (type === "income") {
    const isOtherIncome =
      kind.includes("other") ||
      name.includes("other income") ||
      name.includes("interest income") ||
      name.includes("gain");

    return isOtherIncome ? 13 : 10; // Other Income : Revenue
  }

  if (type === "cogs") {
    return 11; // COGS
  }

  if (type === "expense") {
    const isCogsLike =
      kind.includes("cogs") ||
      kind.includes("cost of goods") ||
      name.includes("cost of goods sold") ||
      name.includes("cogs");

    if (isCogsLike) return 11; // COGS

    const isOtherExpense =
      kind.includes("other") ||
      name.includes("other expense") ||
      name.includes("interest expense") ||
      name.includes("loss");

    return isOtherExpense ? 14 : 12; // Other Expense : Operating Expenses
  }

  return 99;
}

function csvEscape(v: any) {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function buildCsv(headers: string[], rows: (string | number)[][]) {
  return (
    [
      headers.map(csvEscape).join(","),
      ...rows.map((r) => r.map(csvEscape).join(",")),
    ].join("\n") + "\n"
  );
}

// -------------------- Small menu (3 dots) --------------------
function MoreMenu({
  disabled,
  onExport,
}: {
  disabled?: boolean;
  onExport: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    "border border-gray-200 bg-white " +
    "hover:bg-gray-50 active:bg-gray-100 transition " +
    "focus:outline-none focus:ring-2 focus:ring-appleblueD " +
    "disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={iconBtn}
        aria-label={t("directory.common.more")}
      >
        <EllipsisVerticalIcon className="w-5 h-5 text-gray-500" />
      </button>

      {open && !disabled && (
        <div className="absolute right-0 mt-2 w-44 rounded-xl border border-gray-200 bg-white shadow-md z-50 overflow-hidden">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onExport();
            }}
            className={`w-full text-left px-3 py-2 ${A.body} hover:bg-gray-50`}
          >
            {t("directory.common.exportCsv")}
          </button>
        </div>
      )}
    </div>
  );
}

export default function TrialBalance() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { company_id } = useParams<{ company_id: string }>();

  const labelCls = `block ${A.label} mb-1`;

  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    "hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD transition";

  const headerShell =
    "sticky top-0 z-30 bg-gray-50/95 backdrop-blur supports-[backdrop-filter]:bg-gray-50/70 border-b border-gray-100";

  const headerInner =
    "w-full max-w-3xl mx-auto px-2 sm:px-6 pt-4 pb-2 mb-2 flex items-center";

  const amtCol = "w-[92px] min-[390px]:w-24 md:w-28 shrink-0";
  const amtCell =
    `text-right ${A.num} font-medium whitespace-nowrap ` +
    "text-[12px] min-[390px]:text-[13px]";

  // Total row: same responsive size ramp as the account rows, kept semibold.
  // Deliberately does NOT compose A.body, whose text-[13px] would otherwise
  // fight the 12px step below 390px on utility ordering alone.
  const totalAmtCell =
    `text-right ${A.num} font-semibold whitespace-nowrap ` +
    "text-[12px] min-[390px]:text-[13px]";

  const drText = "!text-emerald-700";
  const crText = "!text-rose-700";
  const drHead = "!text-emerald-600";
  const crHead = "!text-rose-600";

  const [asOf, setAsOf] = useState<string>(todayStr());
  const [rows, setRows] = useState<TrialBalanceRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [fetching, setFetching] = useState<boolean>(false);

  const sortOptions: SortOption[] = useMemo(
    () => [
      { id: "alpha", name: "Account (A → Z)" },
      { id: "class", name: "Class order" },
    ],
    [],
  );

  const [sortMode, setSortMode] = useState<SortMode>("alpha");

  const selectedSortObj = useMemo(
    () => sortOptions.find((o) => o.id === sortMode) || sortOptions[0],
    [sortMode, sortOptions],
  );

  const onToolbarSortChange = (opt: SortOption | null) => {
    setSortMode(opt ? opt.id : "alpha");
  };

  useEffect(() => {
    let alive = true;
    const rid = Math.random().toString(36).slice(2);

    (async () => {
      setLoading(true);
      let startForAsOf: string;

      try {
        const cfg = await apiGet<{
          default_start_for_end: string;
          end: string;
        }>(`/year-end/config?end=${asOf}`);
        if (!alive) return;
        startForAsOf = cfg.default_start_for_end;
      } catch {
        const d = new Date(`${asOf}T00:00:00`);
        startForAsOf = `${d.getFullYear()}-01-01`;
      } finally {
        if (alive) setLoading(false);
      }

      setFetching(true);
      const bust = `_=${encodeURIComponent(rid)}`;

      try {
        try {
          const p1 = await apiGet<ApiPayload>(
            `/reports/trial-balance?as_of=${asOf}&${bust}`,
          );
          if (!alive) return;
          const list = Array.isArray(p1) ? p1 : p1?.rows || [];
          setRows(list || []);
          return;
        } catch {}

        try {
          const p2 = await apiGet<ApiPayload>(
            `/reports/trial-balance?start=${startForAsOf}&end=${asOf}&${bust}`,
          );
          if (!alive) return;
          const list = Array.isArray(p2) ? p2 : p2?.rows || [];
          setRows(list || []);
          return;
        } catch {}

        try {
          const p3 = await apiGet<ApiPayload>(
            `/reports/trial-balance?from=${startForAsOf}&to=${asOf}&${bust}`,
          );
          if (!alive) return;
          const list = Array.isArray(p3) ? p3 : p3?.rows || [];
          setRows(list || []);
          return;
        } catch {
          // keep previous rows if all fail
        }
      } finally {
        if (alive) setFetching(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [asOf]);

  const { flatRows, totalDebit, totalCredit } = useMemo(() => {
    const processed = (rows || [])
      .map((r) => {
        const bal =
          typeof r.balance === "number"
            ? r.balance
            : (r.debit || 0) - (r.credit || 0);
        return { ...r, balance: bal };
      });

    let totalDr = 0;
    let totalCr = 0;
    for (const r of processed) {
      const bal = r.balance ?? (r.debit || 0) - (r.credit || 0);
      if (bal >= 0) totalDr += bal;
      else totalCr += -bal;
    }

    const sorted = [...processed].sort((a, b) => {
      if (sortMode === "alpha") {
        return compareText(a.account_name, b.account_name);
      }

      const bucketA = reportBucket(a);
      const bucketB = reportBucket(b);
      if (bucketA !== bucketB) return bucketA - bucketB;

      const codeCmp = compareAccountCode(a.account_code, b.account_code);
      if (codeCmp !== 0) return codeCmp;

      const kindCmp = compareText(a.kind, b.kind);
      if (kindCmp !== 0) return kindCmp;

      return compareText(a.account_name, b.account_name);
    });

    return {
      flatRows: sorted,
      totalDebit: totalDr,
      totalCredit: totalCr,
    };
  }, [rows, sortMode]);

  const exportCsv = () => {
    const headers = ["Account", "Debit", "Credit"];
    const out: (string | number)[][] = flatRows.map((r) => {
      const bal = r.balance ?? (r.debit || 0) - (r.credit || 0);
      const debit = bal >= 0 ? bal : 0;
      const credit = bal < 0 ? -bal : 0;
      return [accountDisplayName(r.account_name, r.account_id), debit || "", credit || ""];
    });

    const csvText = buildCsv(headers, out);
    downloadCsv(`TrialBalance_${asOf}.csv`, csvText);
  };

  const TitleBar = () => (
    <div className={headerShell}>
      <div className={headerInner}>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className={iconBtn}
          aria-label={t("directory.common.back")}
        >
          <ArrowLeftIcon className="w-5 h-5 text-gray-400 hover:text-gray-600 transition" />
        </button>

        <div className="flex-1 flex justify-center pointer-events-none">
          <h2 className={`${A.title} text-center`}>{t("trialBalance.title")}</h2>
        </div>

        <button
          type="button"
          onClick={() => navigate(companyRoute(company_id!, "/"))}
          className={iconBtn}
          aria-label={t("directory.common.close")}
        >
          <XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-600 transition" />
        </button>
      </div>
    </div>
  );

  const clamp2: React.CSSProperties = {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  };

  const TrialRow = ({ row }: { row: TrialBalanceRow }) => {
    const bal = row.balance ?? (row.debit || 0) - (row.credit || 0);
    const debit = bal >= 0 ? bal : 0;
    const credit = bal < 0 ? -bal : 0;

    return (
      <div className="flex items-start gap-x-2 py-2 border-b border-gray-50 last:border-b-0">
        <div className="flex-1 min-w-0">
          <div
            className={`${A.body} leading-snug break-words`}
            style={clamp2}
            title={localizedAccountDisplayName({ name: row.account_name, type: row.type, kind: row.kind }, t, displayAccountName(accountDisplayName(row.account_name, row.account_id)))}
          >
            {localizedAccountDisplayName({ name: row.account_name, type: row.type, kind: row.kind }, t, displayAccountName(accountDisplayName(row.account_name, row.account_id)))}
          </div>
        </div>

        <div className={`${amtCol} ${amtCell} ${debit ? drText : ""}`}>
          {debit ? fmt(debit) : ""}
        </div>

        <div className={`${amtCol} ${amtCell} ${credit ? crText : ""}`}>
          {credit ? fmt(credit) : ""}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div
        className={`flex justify-center items-center min-h-[50vh] text-gray-400 ${A.body}`}
      >
        {t("directory.common.loading")}
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-50 ${A.body}`}>
      <TitleBar />

      <div className="w-full max-w-3xl mx-auto px-2 sm:px-6 mb-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-end">
          {/* Row 1 on mobile / col 1 on desktop */}
          <div className="w-full md:w-[220px]">
            <ReportDateRangeFilter
              companyId={company_id}
              variant="asOf"
              startDate={asOf}
              endDate={asOf}
              onChange={({ endDate }) => setAsOf(endDate)}
              asOfLabel={t("trialBalance.asAt")}
              openCalendarLabel={t("sales.shared.openCalendar")}
            />
          </div>

          {/* Row 2 on mobile: Sort + menu on same row */}
          <div className="flex w-full items-end gap-2 md:w-auto">
            <div className="min-w-0 flex-1 md:w-[220px] md:flex-none relative z-[60]">
              <label className={labelCls}>{t("directory.common.sort")}</label>
              <AppleComboBox<SortOption>
                options={sortOptions}
                value={selectedSortObj}
                onChange={onToolbarSortChange}
                placeholder={t("generalJournal.sort.placeholder")}
                getOptionLabel={(o) => o.id === "alpha" ? t("generalJournal.sort.accountAsc") : t("trialBalance.sort.classOrder")}
                getOptionSearchText={(o) => o.name}
                preserveQueryOnDisplayChange
                emptyText={t("sales.shared.noResults")}
                toggleOptionsLabel={t("sales.shared.toggleOptions")}
              />
            </div>

            <div className="shrink-0 self-end md:pb-[2px]">
              <MoreMenu disabled={!flatRows.length} onExport={exportCsv} />
            </div>
          </div>
        </div>

        {fetching ? (
          <div className={`mt-2 ${A.label}`}>
            {t("trialBalance.recalculating", { date: asOf })}
          </div>
        ) : null}
      </div>

      <div className="w-full max-w-3xl mx-auto px-2 sm:px-6 mb-10">
        <div className={`${A.card} p-3 min-[390px]:p-4 md:p-6`}>
          <div className="flex items-center gap-x-2 text-xs font-medium text-gray-500 border-b border-gray-100 pb-2 mb-1">
            <div className="flex-1 min-w-0">{t("generalJournal.account")}</div>
            <div className={`${amtCol} text-right ${drHead}`}>{t("trialBalance.debit")}</div>
            <div className={`${amtCol} text-right ${crHead}`}>{t("trialBalance.credit")}</div>
          </div>

          {flatRows.length === 0 ? (
            <div className={`py-8 text-gray-400 text-center ${A.body}`}>
              {t("trialBalance.empty")}
            </div>
          ) : (
            <>
              <div className={fetching ? "opacity-60" : ""}>
                {flatRows.map((r, idx) => (
                  <TrialRow
                    key={`${r.account_id ?? r.account_name}-${idx}`}
                    row={r}
                  />
                ))}
              </div>

              <div className="flex items-start gap-x-2 py-2 mt-2 border-t border-gray-200">
                <div className={`${A.body} font-semibold flex-1 min-w-0`}>
                  {t("sales.common.total")}
                </div>
                <div className={`${amtCol} ${totalAmtCell} ${drText}`}>
                  {fmt(totalDebit)}
                </div>
                <div className={`${amtCol} ${totalAmtCell} ${crText}`}>
                  {fmt(totalCredit)}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
